import { useEffect, useRef } from "react";

/**
 * CinemaAudio — procedurally-generated ambient soundscape for Cinema Mode.
 *
 *  - mode="closed": warm low-passed pink noise that simulates a quiet
 *    auditorium murmur (audience settling in before the show).
 *  - mode="open":   a quiet 60 Hz + 120 Hz hum simulating a 35 mm projector
 *    fan, layered with a very faint film grain (high-passed noise).
 *
 * No external assets — built with Web Audio API oscillators + a
 * generated pink-noise buffer. Total weight: ~80 lines, zero KB of audio.
 *
 * Browser autoplay policy: AudioContext only starts after a user gesture.
 * Component guards against that by lazily creating the context only when
 * `enabled` flips to true (which is wired to a user-triggered toggle).
 *
 * Props:
 *   - enabled : boolean — master on/off (must be triggered by a user click
 *                          for AudioContext to be allowed to start)
 *   - mode    : "open" | "closed"
 *   - volume  : 0..1   — master mix level (default 0.6)
 */
/**
 * Premiere swell — a richer one-shot played when the user is a confirmed
 * ticket holder (Premiere Mode). 3-note major-chord swell with a soft
 * "applause" hiss tail. Plays once via `playPremiereSwell` (called from
 * outside the component through a ref).
 */
const playPremiereSwell = (ctx, master) => {
  try {
    const now = ctx.currentTime;
    // C-E-G major triad sweep upward (220, 277, 330 Hz → 880, 1108, 1320)
    [
      { from: 220, to: 880,  vol: 0.18 },
      { from: 277, to: 1108, vol: 0.13 },
      { from: 330, to: 1320, vol: 0.10 },
    ].forEach(({ from, to, vol }) => {
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.setValueAtTime(from, now);
      osc.frequency.exponentialRampToValueAtTime(to, now + 2.4);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(vol, now + 0.6);
      g.gain.linearRampToValueAtTime(vol * 0.6, now + 1.8);
      g.gain.exponentialRampToValueAtTime(0.0001, now + 3.0);
      osc.connect(g).connect(master);
      osc.start(now);
      osc.stop(now + 3.05);
    });
    // Applause-like noise tail (high-passed pink, swelling and fading)
    const buf = ctx.createBuffer(1, ctx.sampleRate * 3, ctx.sampleRate);
    const out = buf.getChannelData(0);
    for (let i = 0; i < out.length; i++) {
      // Modulated noise — applause sparkle
      const env = Math.sin((i / out.length) * Math.PI);
      out[i] = (Math.random() * 2 - 1) * 0.5 * env;
    }
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    const hp = ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 2200;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0, now);
    ng.gain.linearRampToValueAtTime(0.12, now + 1.4);
    ng.gain.exponentialRampToValueAtTime(0.0001, now + 3.0);
    noise.connect(hp).connect(ng).connect(master);
    noise.start(now);
    noise.stop(now + 3.05);
  } catch {}
};

export default function CinemaAudio({ enabled = false, mode = "closed", volume = 0.6, premiereTrigger = 0 }) {
  const ctxRef = useRef(null);
  const masterRef = useRef(null);
  const nodesRef = useRef([]);
  const prevModeRef = useRef(mode);
  const prevPremiereRef = useRef(premiereTrigger);

  // Tear down current playing nodes (without closing the context)
  const stopAll = () => {
    nodesRef.current.forEach((n) => { try { n.stop(); } catch {} try { n.disconnect(); } catch {} });
    nodesRef.current = [];
  };

  // Curtain rise sweep — a brief warm swell that plays when the user transitions
  // from "closed" to "open" curtains. Procedural so no asset is required.
  const playCurtainRise = (ctx, master) => {
    try {
      const now = ctx.currentTime;
      // Two stacked sine sweeps (root + fifth) with an envelope that fades up
      // then settles — feels like an orchestral swell.
      [
        { from: 180, to: 760, vol: 0.18 },
        { from: 270, to: 1140, vol: 0.10 },
      ].forEach(({ from, to, vol }) => {
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.setValueAtTime(from, now);
        osc.frequency.exponentialRampToValueAtTime(to, now + 1.6);
        const g = ctx.createGain();
        g.gain.setValueAtTime(0, now);
        g.gain.linearRampToValueAtTime(vol, now + 0.4);
        g.gain.linearRampToValueAtTime(vol * 0.7, now + 1.2);
        g.gain.exponentialRampToValueAtTime(0.0001, now + 2.0);
        osc.connect(g).connect(master);
        osc.start(now);
        osc.stop(now + 2.05);
      });
    } catch {}
  };

  useEffect(() => {
    if (!enabled) {
      stopAll();
      return;
    }

    // Lazy-init AudioContext on first enable (must follow a user gesture)
    if (!ctxRef.current) {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        ctxRef.current = new Ctx();
        masterRef.current = ctxRef.current.createGain();
        masterRef.current.gain.value = volume;
        masterRef.current.connect(ctxRef.current.destination);
      } catch {
        return;
      }
    }
    const ctx = ctxRef.current;
    if (ctx.state === "suspended") {
      ctx.resume().catch(() => {});
    }
    masterRef.current.gain.value = volume;

    stopAll();

    // If we just transitioned closed→open, play a one-shot orchestral swell
    if (prevModeRef.current === "closed" && mode === "open") {
      playCurtainRise(ctx, masterRef.current);
    }
    prevModeRef.current = mode;

    // Premiere swell — fires once when premiereTrigger increments
    if (premiereTrigger !== prevPremiereRef.current) {
      playPremiereSwell(ctx, masterRef.current);
      prevPremiereRef.current = premiereTrigger;
    }

    if (mode === "open") {
      // 60 Hz + 120 Hz projector hum, very quiet
      [60, 120].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = freq;
        const g = ctx.createGain();
        g.gain.value = i === 0 ? 0.04 : 0.025;
        osc.connect(g).connect(masterRef.current);
        osc.start();
        nodesRef.current.push(osc, g);
      });
      // Faint film-grain hiss (high-passed noise)
      const buf = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
      const out = buf.getChannelData(0);
      for (let i = 0; i < out.length; i++) out[i] = (Math.random() * 2 - 1) * 0.5;
      const noise = ctx.createBufferSource();
      noise.buffer = buf;
      noise.loop = true;
      const hp = ctx.createBiquadFilter();
      hp.type = "highpass";
      hp.frequency.value = 3500;
      const ng = ctx.createGain();
      ng.gain.value = 0.06;
      noise.connect(hp).connect(ng).connect(masterRef.current);
      noise.start();
      nodesRef.current.push(noise, hp, ng);
    } else {
      // Auditorium murmur: warm pink noise, low-passed
      const buf = ctx.createBuffer(1, ctx.sampleRate * 3, ctx.sampleRate);
      const out = buf.getChannelData(0);
      let b0 = 0, b1 = 0, b2 = 0;
      for (let i = 0; i < out.length; i++) {
        const w = Math.random() * 2 - 1;
        b0 = 0.99765 * b0 + w * 0.0990460;
        b1 = 0.96300 * b1 + w * 0.2965164;
        b2 = 0.57000 * b2 + w * 1.0526913;
        out[i] = (b0 + b1 + b2 + w * 0.1848) * 0.18;
      }
      const noise = ctx.createBufferSource();
      noise.buffer = buf;
      noise.loop = true;
      const lp = ctx.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = 900;
      const g = ctx.createGain();
      g.gain.value = 0.55;
      noise.connect(lp).connect(g).connect(masterRef.current);
      noise.start();
      nodesRef.current.push(noise, lp, g);
    }

    return () => { stopAll(); };
  }, [enabled, mode, volume, premiereTrigger]);

  // Close audio context on unmount
  useEffect(() => () => {
    stopAll();
    try { ctxRef.current && ctxRef.current.close(); } catch {}
    ctxRef.current = null;
  }, []);

  return null;
}
